import { computed } from 'vue'
import type { StoryblokStory } from 'storyblok-generate-ts'
import {
  shapeBlogArticleStory,
  shapeBlogOverviewStory,
} from '../utils/blogPayload'
import useCms from '~/composables/cms/useCms'

const BLOG_FOLDER = 'magazin'
const BLOG_TYPE = 'BlogArticleV2'
const BLOG_OVERVIEW_TYPE = 'BlogOverviewV2'

export default <T extends Record<string, any>>(
  key: string,
  isPaginatedData: boolean = false,
) => {
  const { data, fetching, fetchBySlug, fetchByFolder, metaLinks, excluded } =
    useCms<T>(key)

  const fetchMany = async (args: {
    tagIds?: (string | null)[]
    perPage?: number
    page?: number
    resolveTags?: boolean
  }) => {
    await fetchByFolder(BLOG_FOLDER, {
      contentType: BLOG_TYPE,
      perPage: args.perPage,
      page: args.page,
      resolveRelations: [args.resolveTags && BLOG_TYPE + '.tags']
        .filter(Boolean)
        .join(','),
      ...(args.tagIds?.length && {
        filterQuery: { tags: { any_in_array: args.tagIds.join(',') } },
      }),
    })
  }

  const fetchSingle = async (args: { slug: string }) => {
    await fetchBySlug(args.slug, {
      resolveRelations: ['tags', 'teasers', 'more_articles']
        .map((name) => `${BLOG_TYPE}.${name}`)
        .join(','),
    })
    data.value = shapeBlogArticleStory(data.value as StoryblokStory<T>) as any
  }

  const fetchBlogOverview = async () => {
    await fetchBySlug(`${BLOG_FOLDER}`, {
      resolveRelations: [
        BLOG_OVERVIEW_TYPE + '.tags',
        BLOG_OVERVIEW_TYPE + '.more_articles',
      ].join(','),
    })
    data.value = shapeBlogOverviewStory(data.value as StoryblokStory<T>) as any
  }

  return {
    fetchMany,
    fetchSingle,
    fetchBlogOverview,
    data: computed(() => {
      if (isPaginatedData && data.value && 'stories' in data.value) {
        return data.value.stories as unknown as StoryblokStory<T>
      }

      return data.value
    }),
    // Paginated folder fetches return { stories, perPage, total }; surface
    // total so pages can compute lastPage (mirrors useOutfitsFolder).
    total: computed(() =>
      data.value && 'total' in data.value ? Number(data.value.total) : 0,
    ),
    fetching,
    metaLinks,
    excluded,
    BLOG_FOLDER,
    BLOG_TYPE,
  }
}
